import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Image from 'next/image'
import React from 'react'
import { useFormContext } from 'react-hook-form'

import { useFileUpload } from '../fields/ImageUpload'
import { FormValues } from '../forms/ThemeForm'
import Button from 'components/Button'
import Fieldset from 'components/fields/Fieldset'
import { AddImageIcon, SwapIcon } from 'components/icons'

const HiddenInput = styled.input`
	display: none;
`

const ThemeImage = styled(Image)`
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-top-left-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	border-top-right-radius: ${({ theme }) => theme.borderRadiuses.xxl};
`

export const loadingPulse = keyframes`
	from {
		opacity: 0.2;
		color: transparent;
	}

	to {
		opacity: 1;
		color: inherit;
	}
`

const ImagePlaceholder = styled.span<{ isLoading: boolean }>`
	height: 34px;
	width: 34px;
	display: flex;
	gap: ${({ theme }) => theme.spaces.space2};
	align-items: center;
	justify-content: center;
	animation: ${({ isLoading }) => (isLoading ? loadingPulse : 'none')} 800ms ease-in-out infinite alternate;
`

const StyledFieldset = styled(Fieldset)`
	position: relative;
	width: 100%;
	height: 100%;
	padding: 0;
	margin: 0;
	border-top-left-radius: ${({ theme }) => theme.borderRadiuses.round};
	border-top-right-radius: ${({ theme }) => theme.borderRadiuses.round};
`

const RemoveButton = styled(Button)`
	position: absolute;
	right: -18px;
	bottom: 6px;
`

const UploadButton = styled(Button)`
	width: 100%;
	height: 100%;
	background-color: transparent;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space1};
`

const ErrorMessage = styled.span<{ alignBottom?: boolean }>`
	color: ${({ theme }) => theme.colorPalette.red[500]};
	${({ theme }) => theme.typography.smallcaps};
	white-space: normal;

	${({ alignBottom }) =>
		alignBottom &&
		css`
			position: absolute;
			bottom: -40px;
		`}
`

const SwapImageButton = styled.button`
	display: flex;
	top: -8px;
	left: -8px;
	position: absolute;
	box-shadow: ${({ theme }) => theme.boxShadows.md};
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};
	background-color: ${({ theme }) => theme.colors.light.surface};
	padding: 5px;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 200ms ${({ theme }) => theme.transitions.easings.css.smooth};
	width: 27px;
	height: 27px;
	pointer-events: none;
	opacity: 0;
	&:focus-visible {
		opacity: 1;
		pointer-events: auto;
	}
`

const PreviewWrapper = styled.div`
	width: 100%;
	height: 100%;

	&:hover {
		opacity: 0.9;
		${SwapImageButton} {
			opacity: 1;
			pointer-events: auto;
		}
	}
`

const kind = 'header_background_image'
const fieldName = 'headerBackgroundImage'

const HeaderBackgroundUpload = () => {
	const { setValue, clearErrors, trigger } = useFormContext<FormValues>()
	const { onRemoveClick, onUploadClick, fileUrl, isImageUploading, handleFileChange, fileInputRef, error } =
		useFileUpload(kind, fieldName, '')
	const [uiError, setUiError] = React.useState<string | null>(null)

	const showUploadButton = !fileUrl || isImageUploading

	return (
		<StyledFieldset data-testid={`${kind}-image-upload`}>
			{showUploadButton ? (
				<>
					<UploadButton type="button" onClick={onUploadClick} mediumDesktop compact>
						<ImagePlaceholder isLoading={isImageUploading} data-testid="image-placeholder">
							<AddImageIcon />
							{!isImageUploading && 'Upload'}
						</ImagePlaceholder>
					</UploadButton>
					{error && <ErrorMessage alignBottom>{error}</ErrorMessage>}
				</>
			) : (
				<>
					<PreviewWrapper>
						<ThemeImage onClick={onUploadClick} src={fileUrl} alt="header background image" width="100" height="100" />
						<SwapImageButton type="button" onClick={onUploadClick} aria-label="replace image">
							<SwapIcon />
						</SwapImageButton>
						{uiError && <ErrorMessage>{uiError}</ErrorMessage>}
					</PreviewWrapper>
					<RemoveButton
						type="button"
						onClick={() => {
							onRemoveClick()
							trigger('headerBackgroundType')
						}}
						mediumDesktop
						compact
					>
						Remove
					</RemoveButton>
				</>
			)}
			<HiddenInput
				type="file"
				accept="image/png,image/jpeg"
				ref={fileInputRef}
				onChange={(event) => {
					handleFileChange(event, showUploadButton ? undefined : setUiError, () => {
						setUiError('')
						clearErrors()
					}).then(() => {
						setValue('headerBackgroundType', 'image', { shouldValidate: true, shouldDirty: true })
					})
				}}
			/>
		</StyledFieldset>
	)
}

export default HeaderBackgroundUpload
